import {
  ENFORCEMENT_BREAK_MINUTES,
  isBreakPauseSuppression,
} from "./enforcementBreak";
import { MINUTES_PER_DAY } from "./ruleDerivation";
import type { Rule, RuleConfig } from "./ruleTypes";

/** Local minute-of-day 0–1439 from device clock. */
export function getMinuteOfDay(now: Date): number {
  return now.getHours() * 60 + now.getMinutes();
}

export type DeviceScheduleClock = {
  dayOfWeek: number;
  minuteOfDay: number;
  nowMs: number;
};

/**
 * True when the rule is enabled, not currently suppressed, today's weekday is
 * in `days`, and the current local minute falls in
 * `[window.startMinute, window.startMinute + duration)`.
 *
 * Scheduled-rule windows always fit within a single day (normalizeRuleWindow),
 * so this reduces to a same-day check. Quick sessions are exempt from that cap
 * (normalizeQuickSessionWindow) and can end after midnight; in that case the
 * window is also checked against yesterday's `days` entry for the wrapped
 * portion after midnight.
 */
function isRuleInActiveWindow(rule: RuleConfig, clock: DeviceScheduleClock): boolean {
  const minute = clock.minuteOfDay;
  const day = clock.dayOfWeek;
  const { startMinute, durationMinutes } = rule.window;
  const endMinute = startMinute + durationMinutes;

  if (endMinute <= MINUTES_PER_DAY) {
    return rule.days.includes(day) && minute >= startMinute && minute < endMinute;
  }

  const wrappedEndMinute = endMinute - MINUTES_PER_DAY;
  const previousDay = (day + 6) % 7;
  if (rule.days.includes(day) && minute >= startMinute) return true;
  return rule.days.includes(previousDay) && minute < wrappedEndMinute;
}

/** Schedule/window match only — ignores `suppressedUntil` (break pause or full stop). */
export function wouldRuleEnforceIgnoringSuppression(
  rule: RuleConfig,
  clock: DeviceScheduleClock,
): boolean {
  if (!rule.enabled) return false;
  return isRuleInActiveWindow(rule, clock);
}

export function isRuleEnforcingAt(rule: RuleConfig, clock: DeviceScheduleClock): boolean {
  if (!rule.enabled) return false;
  if (rule.suppressedUntil !== undefined && clock.nowMs < rule.suppressedUntil) return false;
  return isRuleInActiveWindow(rule, clock);
}

/** True when actively enforcing or on a temporary break pause (stoppable / break-restartable). */
export function isRuleStoppableAt(rule: RuleConfig, clock: DeviceScheduleClock): boolean {
  if (isRuleEnforcingAt(rule, clock)) return true;
  return (
    isBreakPauseSuppression(rule.suppressedUntil, clock.nowMs, ENFORCEMENT_BREAK_MINUTES) &&
    wouldRuleEnforceIgnoringSuppression(rule, clock)
  );
}

export function isRuleEnforcingNow(rule: RuleConfig, now: Date = new Date()): boolean {
  return isRuleEnforcingAt(rule, {
    dayOfWeek: now.getDay(),
    minuteOfDay: getMinuteOfDay(now),
    nowMs: now.getTime(),
  });
}

/** Rules that are schedule-enforcing at `now`, preserving input order. */
export function getEnforcingRules(rules: Rule[], now: Date = new Date()): Rule[] {
  return rules.filter((rule) => isRuleEnforcingNow(rule, now));
}

/**
 * Rules iOS must keep registered with DeviceActivity — every enabled recurring
 * window, including ones that are not active right now (and including swipe-to-
 * stop / break suppression). Quick sessions are one-shot: include them only while
 * their window is still open so a daily-repeating monitor is not left behind.
 *
 * Shields still apply only to rules that are active *now* (`ShieldApplicator` /
 * `isRuleEnforcingNow`). This list is the monitoring plan, not the live shield set.
 */
export function getNativeScheduledRules(rules: Rule[], now: Date = new Date()): Rule[] {
  const clock: DeviceScheduleClock = {
    dayOfWeek: now.getDay(),
    minuteOfDay: getMinuteOfDay(now),
    nowMs: now.getTime(),
  };
  return rules.filter((rule) => {
    if (!rule.enabled) return false;
    if (rule.source === "quick_session") {
      return wouldRuleEnforceIgnoringSuppression(rule, clock);
    }
    return true;
  });
}

/**
 * True when the user stopped this scheduled rule via swipe-to-unlock (full stop),
 * not a short break pause. The rule stays enabled and inside its window but
 * `suppressedUntil` blocks enforcement until the next schedule activation or
 * manual resume.
 */
export function isUserForcedStopSuppression(
  rule: RuleConfig,
  clock: DeviceScheduleClock,
  breakMinutes: number = ENFORCEMENT_BREAK_MINUTES,
): boolean {
  if (!rule.enabled) return false;
  if (rule.source === "quick_session") return false;
  if (!wouldRuleEnforceIgnoringSuppression(rule, clock)) return false;
  if (rule.suppressedUntil === undefined || clock.nowMs >= rule.suppressedUntil) return false;
  return !isBreakPauseSuppression(rule.suppressedUntil, clock.nowMs, breakMinutes);
}

/** Scheduled rules forcibly suspended by the user until the next activation. */
export function getRulesOnForcedStop(rules: Rule[], now: Date = new Date()): Rule[] {
  const clock: DeviceScheduleClock = {
    dayOfWeek: now.getDay(),
    minuteOfDay: getMinuteOfDay(now),
    nowMs: now.getTime(),
  };
  return rules.filter((rule) => isUserForcedStopSuppression(rule, clock));
}

/**
 * Rules temporarily paused via a break (not a swipe-to-unlock full stop). Shown in
 * orange pause UI while native enforcement stays lifted.
 */
export function getRulesOnBreakPause(rules: Rule[], now: Date = new Date()): Rule[] {
  const clock: DeviceScheduleClock = {
    dayOfWeek: now.getDay(),
    minuteOfDay: getMinuteOfDay(now),
    nowMs: now.getTime(),
  };
  return rules.filter(
    (rule) =>
      isBreakPauseSuppression(rule.suppressedUntil, clock.nowMs, ENFORCEMENT_BREAK_MINUTES) &&
      wouldRuleEnforceIgnoringSuppression(rule, clock),
  );
}

/** Milliseconds until the earliest active break pause ends. */
export function msUntilBreakEnds(rules: Rule[], now: Date = new Date()): number {
  const nowMs = now.getTime();
  let earliest: number | null = null;
  for (const rule of getRulesOnBreakPause(rules, now)) {
    const end = rule.suppressedUntil;
    if (end === undefined) continue;
    if (earliest === null || end < earliest) earliest = end;
  }
  if (earliest === null) return 0;
  return Math.max(0, earliest - nowMs);
}

/**
 * Local timestamp (ms) when `rule`'s current enforcement window ends, or null
 * when the rule is not enforcing at `now`.
 */
export function ruleEnforcementEndTimestamp(rule: RuleConfig, now: Date = new Date()): number | null {
  if (!isRuleEnforcingNow(rule, now)) return null;

  const minute = getMinuteOfDay(now);
  const { startMinute, durationMinutes } = rule.window;
  const endMinute = startMinute + durationMinutes;

  if (endMinute <= MINUTES_PER_DAY) {
    const end = new Date(now);
    end.setHours(0, 0, 0, 0);
    end.setMinutes(endMinute);
    return end.getTime();
  }

  const wrappedEndMinute = endMinute - MINUTES_PER_DAY;
  const previousDay = (now.getDay() + 6) % 7;

  if (rule.days.includes(now.getDay()) && minute >= startMinute) {
    const end = new Date(now);
    end.setHours(0, 0, 0, 0);
    end.setMinutes(endMinute);
    return end.getTime();
  }

  if (rule.days.includes(previousDay) && minute < wrappedEndMinute) {
    const end = new Date(now);
    end.setHours(0, 0, 0, 0);
    end.setMinutes(wrappedEndMinute);
    return end.getTime();
  }

  return null;
}

/** Latest end timestamp among rules enforcing at `now`, or null when none. */
export function latestEnforcementEndTimestamp(rules: Rule[], now: Date = new Date()): number | null {
  let latest: number | null = null;
  for (const rule of rules) {
    const end = ruleEnforcementEndTimestamp(rule, now);
    if (end !== null && (latest === null || end > latest)) {
      latest = end;
    }
  }
  return latest;
}

/** Milliseconds until the longest active enforcement window ends. */
export function msUntilEnforcementEnds(rules: Rule[], now: Date = new Date()): number {
  const latestEnd = latestEnforcementEndTimestamp(rules, now);
  if (latestEnd === null) return 0;
  return Math.max(0, latestEnd - now.getTime());
}

/** Live countdown label: `2h 15m` above an hour, `MM:SS` under an hour. */
export function formatEnforcementRemainingMs(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  }

  const paddedMinutes = String(minutes);
  const paddedSeconds = String(seconds).padStart(2, "0");
  return `${paddedMinutes}:${paddedSeconds}`;
}

/**
 * Timestamp (ms) of the next time `rule`'s own schedule (`days` + `window`)
 * will start enforcing, strictly after `now`. Used to suppress a currently
 * enforcing scheduled rule until its next legitimate activation without
 * touching `days`/`window`/`targetIds`.
 *
 * Scheduled-rule windows never cross midnight (normalizeRuleWindow), so only
 * same-day windows need to be considered. Checks today first — valid only if
 * today is a scheduled day and `window.startMinute` hasn't passed yet — then
 * each of the next 7 days, wrapping back to today's weekday as the fallback
 * (next week) when `days` contains only today.
 */
export function computeNextActivation(
  rule: Pick<RuleConfig, "days" | "window">,
  now: Date = new Date(),
): number {
  const days = new Set(rule.days);
  const nowMinute = getMinuteOfDay(now);
  const { startMinute } = rule.window;

  for (let offset = 0; offset <= 7; offset += 1) {
    const day = (now.getDay() + offset) % 7;
    if (!days.has(day)) continue;
    if (offset === 0 && nowMinute >= startMinute) continue;
    return new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset, 0, startMinute, 0, 0).getTime();
  }

  // Unreachable for normalized rules (`normalizeDays` never returns an empty
  // array), but keeps the function total for arbitrary input.
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7, 0, startMinute, 0, 0).getTime();
}
